import React, { useState } from "react";
import PropTypes from "prop-types";
import Select from "react-select";

import "./Sidebar.css";

function Sidebar({
    currentMatrix,
    currentMatrixId, 
    currentMatrixName,
    matrixFiles,
    zoom,
    growthPeriod,
    handleSelectMatrix,
    handleShowReturns,
    handleSelectInflation,
    handleSelectTimeframe,
    handleZoom,
    handleShowMetrics,
    handleShowComparisons,
    handleTimeframeSubmit
}) {
    /*
        Build an X and Y hashtable with FROM and TO years
        So we can map the years to coordinates. For example:

        x = { 1990: 0, 1991: 1, ...} y = { 2019: 0, 2018: 1, ...}

        When user asks for FROM:1990 & TO:2019, we can look up the coordinates as [0, 0].

    */
   const { meta: { timePeriod }} = currentMatrix;
   const [startYear, endYear] = timePeriod; // index matrix time period
   const [start, end] = growthPeriod; // selected time period
   const numberOfRows = endYear - startYear;
   
   let xMap = {}; // TO
   let yMap = {}; // FROM
   let tempStart = startYear;
   let tempEnd = endYear;

    // equivalent to do N times
    Array.from({length: numberOfRows + 1}).forEach((item, index) => {
        xMap[tempStart] = index;
        yMap[tempEnd] = index;
        tempStart = tempStart + 1;
        tempEnd = tempEnd - 1;
    });

    const [timeframeFrom, setTimeframeFrom ] = useState();
    const [timeframeTo, setTimeframeTo ] = useState();

    const handleChangeTimeframeFrom = (event) => {
        const { value } = event.target;
        setTimeframeFrom(value);
    };
    const handleChangeTimeframeTo = (event) => {
        const { value } = event.target;
        setTimeframeTo(value);
    };
    const handleSubmitTimeframe = (event) => {
        // need to check if timeframe is valid
        const from = xMap[timeframeFrom];
        const to = yMap[timeframeTo];
        
        // check if they are numbers because checking for 0 && 0 will not work
        if (typeof from === 'number' && typeof to === 'number') {
            handleTimeframeSubmit([to, from]);
        }
        event.preventDefault();
        return false;
    };
    
    const timeframesToHighlight = [
        { value: '[0, 0]',   label: 'None' },
        { value: '[0, 5]',   label: '1-5' },
        { value: '[5, 10]',  label: '6-10' },
        { value: '[10, 15]', label: '11-15' }
    ];
    const zoomOptions = [
        // { value: 0.25,  label: '25%' },
        { value: 0.5,   label: '50%' },
        { value: 0.75,  label: '75%' },
        { value: 1,     label: '100%' },
        { value: 2,     label: '200%' },
        { value: 3,     label: '300%' },
        { value: 4,     label: '400%' },
    ];
    const defaultZoomOption = zoomOptions.filter(item => item.value === zoom);
    const handleChangeShowReturns = (event) => {
        const { value } = event.target;
        return value === "yes" ? handleShowReturns(true) : handleShowReturns(false);
    }
    const handleChangeInflation = (event) => {
        const { value } = event.target;
        return value === "after" ? handleSelectInflation(true) : handleSelectInflation(false);
    }
    const handleFormSubmit = (event) => {
        event.preventDefault();
        return false;
    };

    // Group similar indexes in the dropdown and sort them by a predefined order
    let groups = {};
    matrixFiles.forEach((item) => {
        const { value, label, group, order } = item;
        if (!groups[group]) {
            groups[group] = [];
        }
        // remove FamaFrench and Index from visual label in dropdown
        const formattedLabel = label.replace('Fama/French', '').replace('Index', '');
        groups[group].push({
            value, 
            label: formattedLabel, 
            group, 
            order
        });
    })
    const groupedOptions = Object.entries(groups).map(([key, value]) => {
        const { order } = value[0];
        return {
            label: key,
            options: value,
            order: order
        }
    }).sort((a, b) => (a.order > b.order) ? 1 : -1);

    const selectIndexStyles = {
        groupHeading: styles => ({ 
            ...styles, 
            fontSize: '0.9rem',
            fontWeight: 'bold',
            textTransform: 'none',
            color: 'black',
        }),
      };

    return (
        <React.Fragment>
            <form onSubmit={handleFormSubmit}>
                <div className="form__top">
                    {/* Choose an Index */}
                    <div className="form__group">
                        <div className="app__title">
                            The Index Matrix<sup>©</sup>
                        </div>
                        <div className="app__subtitle">
                            Patience Rewarded
                        </div>   
                    </div>
                    <div className="form__group">
                        <div className="form__title">Choose an Index</div>
                        <Select
                            name="index-selector"
                            className="index-selector"
                            value={currentMatrixId}
                            placeholder={currentMatrixName}
                            options={groupedOptions}
                            isMulti={false}
                            isSearchable={false}
                            styles={selectIndexStyles}
                            onChange={({ value }) => handleSelectMatrix(value, currentMatrixName)}
                        />
                    </div>
                </div>
                <div className="form__bottom">
                    {/* Inflation */}
                    <div className="form__group--inline">
                        <div className="form__title">Inflation:</div>
                        <label className="radio-inline">
                            <input type="radio" name="inflation" value="before" onChange={handleChangeInflation} defaultChecked />
                            Before
                        </label>
                        <label className="radio-inline">
                            <input type="radio" name="inflation" value="after" onChange={handleChangeInflation} />
                            After
                        </label>
                    </div>

                    {/* Show Retruns */}
                    <div className="form__group--inline">
                        <div className="form__title">Show Returns?</div>
                        <label className="radio-inline">
                            <input type="radio" name="showreturns" value="yes" onChange={handleChangeShowReturns} />
                            Yes
                        </label>
                        <label className="radio-inline">
                            <input type="radio" name="showreturns" value="no" onChange={handleChangeShowReturns} defaultChecked />
                            No
                        </label>
                    </div>

                    {/* Show Returns */}
                    <div className="form__group">
                        <div className="form__title">Timeframe</div>
                        <div className="form__group--inline ">
                            <label className="input-inline">
                                From
                                <input 
                                    type="number" 
                                    name="timeframe_from" 
                                    placeholder={start} /* display the current timeframe as the placeholder */
                                    min={startYear} /* min and max should the min and max of the matrix, not the selected */
                                    max={endYear}
                                    onChange={handleChangeTimeframeFrom} 
                                    />
                            </label>
                            <label className="input-inline">
                                To
                                <input 
                                    type="number"
                                    name="timeframe_to"
                                    placeholder={end} /* display the current timeframe as the placeholder */
                                    min={startYear}  /* min and max should the min and max of the matrix, not the selected */
                                    max={endYear}
                                    onChange={handleChangeTimeframeTo} 
                                />
                            </label>
                            <button className="form__button" disabled={!timeframeFrom || !timeframeTo } onClick={handleSubmitTimeframe}>Update</button>
                        </div>
                    </div>

                    {/* Timeframe */}
                    <div className="form__group">
                        <div className="form__title">Highlight Year Range</div>
                        <div className="form__group--inline ">
                            {timeframesToHighlight.map(({ value, label}, index) => (
                                <label className="radio-inline" key={value}>
                                    <input 
                                        type="radio" 
                                        name="timeframe" 
                                        value={value} 
                                        defaultChecked={index === 0 ? true : false } 
                                        onChange={() => handleSelectTimeframe(JSON.parse(value))}
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Zoom */}
                    <div className="form__group form__group--zoom">
                        <div className="form__group--inline">
                            <div className="form__title">Zoom</div>
                            <Select
                                name="zoom-selector"
                                className="zoom-selector"
                                defaultValue={defaultZoomOption}
                                options={zoomOptions}
                                menuPlacement="top"
                                onChange={({ value }) => handleZoom(value)}
                            />
                            <button className="form__button" onClick={() => {handleZoom(1)}}>Reset</button>
                        </div>
                    </div>

                    {/* Show Metrics */}
                    <div className="form__group">
                        <button className="form__button" onClick={handleShowMetrics}>Show Metrics</button>
                    </div>

                    {/* Show Index Comparisons */}
                    <div className="form__group">
                        <button className="form__button" onClick={handleShowComparisons}>Show Index Comparisons</button>
                    </div>
                </div>
            </form>
        </React.Fragment>
    );
}

Sidebar.propTypes = {
    currentMatrix: PropTypes.any.isRequired, // TODO: this can probably be passed in via currentMatrix object
    currentMatrixId: PropTypes.string.isRequired, // TODO: this can probably be passed in via currentMatrix object
    currentMatrixName: PropTypes.string.isRequired,
    matrixFiles: PropTypes.arrayOf(PropTypes.shape({
		value: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		group: PropTypes.string.isRequired,
	})).isRequired,
    zoom: PropTypes.number.isRequired,
    handleSelectMatrix: PropTypes.func.isRequired,
    handleShowReturns: PropTypes.func.isRequired,
    handleShowMetrics: PropTypes.func.isRequired,
    handleShowComparisons: PropTypes.func.isRequired,
    handleSelectTimeframe: PropTypes.func.isRequired,
    handleZoom: PropTypes.func.isRequired,
    handleTimeframeSubmit: PropTypes.func.isRequired,
};

export default Sidebar;
